import React, { ComponentProps, FC, ReactNode } from 'react';

import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha, styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';

import ExpandRoundedIcon from '~/assets/icons/ExpandRoundedIcon';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import {
  MIDDLE_MARGIN,
  MIDDLE_RADIUS_PX,
  SUBMIDDLE_MARGIN_PX,
  TINY_MARGIN,
  TINY_MARGIN_PX,
} from '~/theme';
import { Maybe } from '~/types';
import lineClamp from '~/ui/components/lineClamp';

interface TermProps {
  children: ReactNode;
  link: string;
  title?: Maybe<string>;
  definition?: Maybe<string>;
}

const TermTooltip = styled(({ className, ...props }: ComponentProps<typeof Tooltip>) => (
  <Tooltip {...props} componentsProps={{ tooltip: { className } }} />
))`
  max-width: 345px;
  margin-right: 0;
  padding: 0;
  background: none;
  filter: drop-shadow(0px 3px 6px ${({ theme }) => alpha(theme.palette.common.blackPure, 0.5)});

  & .MuiTooltip-arrow {
    color: ${({ theme }) => theme.palette.common.violetAnalyticsCard};
  }
`;

const Term: FC<TermProps> = ({ title, definition, link, children }) => {
  const { t } = useTenantTranslation();

  return (
    <TermTooltip
      arrow
      placement={'top'}
      title={
        <Wrapper>
          <Title>{title}</Title>
          <Definition>{definition}</Definition>
          <StyledLink to={link}>
            {t('button.readMore')}
            <StyledExpandRoundedIcon />
          </StyledLink>
        </Wrapper>
      }
    >
      <TermLink to={link}>{children}</TermLink>
    </TermTooltip>
  );
};

export default Term;

const TermLink = styled(Link)`
  background: ${({ theme }) => theme.palette.common.transparent};
  color: ${({ theme }) => theme.palette.common.blue};
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`;
const Wrapper = styled('div')`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: ${SUBMIDDLE_MARGIN_PX} ${SUBMIDDLE_MARGIN_PX} ${SUBMIDDLE_MARGIN_PX}
    ${MIDDLE_MARGIN + TINY_MARGIN}px;
  gap: ${TINY_MARGIN_PX};
  border-radius: ${MIDDLE_RADIUS_PX};
  background: ${({ theme }) => theme.palette.common.violetAnalyticsCard};

  &:after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 8px;
    height: 100%;
    border-top-left-radius: inherit;
    border-bottom-left-radius: inherit;
    background: ${({ theme }) => theme.palette.common.primary};
  }
`;
const Definition = styled(Typography)`
  font-size: 14px;
  color: ${({ theme }) => theme.palette.common.gray};
  ${lineClamp(5)}
`;
const StyledLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  color: ${({ theme }) => theme.palette.common.violetSecondary};
  transition: color 0.3s;

  &:hover {
    color: ${({ theme }) => theme.palette.common.accent};
  }
`;
const Title = styled(Typography)`
  font-weight: 700;
`;
const StyledExpandRoundedIcon = styled(ExpandRoundedIcon)`
  width: 18px;
  height: 18px;
  color: inherit;
`;
